import React, { createContext, useContext, ReactNode, useState } from 'react'
import { apiResponse, ResponseType, showSnackbar } from '../../utils/Utils'
import { useAuth } from '../auth-context/AuthContext'
import { useFetchWithAuth } from '../../hooks/useFetchWithAuth'
import { Status, User } from '../../lib/EventLib'
import { VariantType } from '../../lib/CommonLib'

interface EventViewContextType {
  eventFilter: string
  setEventFilter: (val: string) => void
  events: any
  setEvents: (val: any) => void
  page: number
  setPage: (val: number) => void
  rowsPerPage: number
  setRowsPerPage: (val: number) => void
  createEvent: (val: any) => void
  saveEvent: (id: number, body: any) => void
  status: Status[]
  eventType: Status[]
  assignedToList: User[]
  fetchEvents: () => void
  fetchStatus: () => void
  fetchType: () => void
  fetchAssignedToList: () => void
}

const EventContext = createContext<EventViewContextType | undefined>(undefined)

export const EventViewProvider = ({ children }: { children: ReactNode }) => {
  const { setSnackbarOpen, setSnackbarMessage, setVariant, setLoading } = useAuth()
  const fetchWithAuth = useFetchWithAuth()
  const [eventFilter, setEventFilter] = useState<string>('')
  const [events, setEvents] = useState<any>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [status, setStatus] = useState<Status[]>([])
  const [eventType, setEventType] = useState<Status[]>([])
  const [assignedToList, setAssignedToList] = useState<User[]>([])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Event', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      const reverseData = await data.reverse()
      setEvents(reverseData)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const fetchStatus = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Event/Statuses', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setStatus(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setStatus([])
    } finally {
      setLoading(false)
    }
  }

  const fetchType = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Event/EventTypes', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setEventType(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setEventType([])
    } finally {
      setLoading(false)
    }
  }

  const fetchAssignedToList = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Event/AssignedTo', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setAssignedToList(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setAssignedToList([])
    } finally {
      setLoading(false)
    }
  }

  const createEvent = async (eventData: any) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      })
      const data = await apiResponse(response)
      const eventId = Number(data.id)
      await fetchEvents()
      showSnackbar(
        'New event successfully added.',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
      const userId = eventData.assignedUserId
      if (userId) {
        await createEventAssign(eventId, userId)
      }
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const createEventAssign = async (eventId: number, userId: string) => {
    try {
      setLoading(true)
      const url = `/api/Event/Assign?eventId=${eventId}&userId=${userId}`

      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response, ResponseType.text)
      showSnackbar(`${data}`, VariantType.success, setVariant, setSnackbarOpen, setSnackbarMessage)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const saveEvent = async (eventId: number, eventData: any): Promise<void> => {
    try {
      setLoading(true)
      const editedData = {
        ...eventData,
        status: eventData.status,
        remarks: eventData.remarks,
        assignedUserId: eventData.assignedUserId,
      }
      const response = await fetchWithAuth(`/api/Event/${eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editedData),
      })
      const updatedData = await apiResponse(response)
      setEvents((prevEvents: any[]) => {
        const updatedEvents = prevEvents.map((event) => {
          if (event.id === updatedData.id) {
            return { ...event, ...updatedData }
          }
          return event
        })
        return updatedEvents
      })
      showSnackbar(
        `Successfully updated event.`,
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
      const userId = eventData.assignedUserId
      if (userId) {
        await createEventAssign(eventId, userId)
      }
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <EventContext.Provider
      value={{
        eventFilter,
        setEventFilter,
        events,
        setEvents,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        createEvent,
        saveEvent,
        status,
        eventType,
        assignedToList,
        fetchEvents,
        fetchStatus,
        fetchType,
        fetchAssignedToList,
      }}
    >
      {children}
    </EventContext.Provider>
  )
}

export const useEventView = () => {
  const context = useContext(EventContext)
  if (context === undefined) {
    throw new Error('useEventView must be used within an EventViewProvider')
  }
  return context
}
